import { format, unwrapFunctionStore } from 'svelte-i18n';
import { UAParser } from 'ua-parser-js';

import { ACTIVITY_EVENTS } from '@client/enums';

import { staticHexColors } from '@common/utils/colors';
import { daysBetweenDates } from '@common/utils/dates';

const $format = unwrapFunctionStore(format);

const unknown = $format('label.UNKNOWN');

/**
 * @typedef {Object} ActivityNote
 * @property {string} [ua]
 * @property {string} [country]
 * @property {string} [city]
 * @property {string} [objtype]
 *
 * @typedef {Object} ActivityEntry
 * @property {string} [verb]
 * @property {string} [recipient]
 * @property {ActivityNote} [note]
 */

/**
 * Parses a user agent string into device/browser/os info.
 * @param {string} ua
 * @returns {import('ua-parser-js').UAParserInstance}
 */
export function userAgentMeta(ua) {
  return UAParser(ua);
}

/**
 * Groups activity data by type: devices, OS, browsers, locations, events.
 * @param {ActivityEntry[]} data
 * @returns {{
 *   events: Record<string, { total: number }>,
 *   cities: Record<string, { total: number }>,
 *   countries: Record<string, { total: number }>,
 *   device: Record<string, { total: number }>,
 *   os: Record<string, { total: number }>,
 *   browser: Record<string, { total: number }>
 * }}
 */
export const groupByTypes = (data) => {
  return data.reduce(
    (acc, config) => {
      if (config?.note?.ua) {
        const ua = userAgentMeta(config.note.ua);
        const device = ua.device.vendor ? `${ua.device.vendor} ${ua.device.model}` : unknown;
        const os = ua.os.name ? `${ua.os.name} ${ua.os.version}` : unknown;
        const browser = ua.browser.name ? `${ua.browser.name} ${ua.browser.version}` : unknown;

        if (!acc.device[device]) {
          acc.device[device] = {
            total: 1
          };
        } else {
          acc.device[device].total += 1;
        }

        if (!acc.os[os]) {
          acc.os[os] = {
            total: 1
          };
        } else {
          acc.os[os].total += 1;
        }

        if (!acc.browser[browser]) {
          acc.browser[browser] = {
            total: 1
          };
        } else {
          acc.browser[browser].total += 1;
        }
      }

      if (config?.note?.country) {
        const country = !config?.note?.country || config?.note?.country === '-' ? unknown : config?.note?.country;

        if (!acc.countries[country]) {
          acc.countries[country] = {
            total: 1
          };
        } else {
          acc.countries[country].total += 1;
        }
      }

      if (config?.note?.city) {
        const country = !config?.note?.country || config?.note?.country === '-' ? unknown : config?.note?.country;
        const city = !config?.note?.city || config?.note?.city === '-' ? unknown : `${config?.note?.city}, ${country}`;

        if (!acc.cities[city]) {
          acc.cities[city] = {
            total: 1
          };
        } else {
          acc.cities[city].total += 1;
        }
      }

      if (config?.note?.objtype && config?.verb !== 'tracked' && config?.verb !== 'embedded') {
        const event =
          $format(ACTIVITY_EVENTS[config?.verb]?.label) ||
          $format(ACTIVITY_EVENTS[config?.note?.objtype]?.label) ||
          config?.note?.objtype;

        if (!acc.events[event]) {
          acc.events[event] = {
            total: 1
          };
        } else {
          acc.events[event].total += 1;
        }
      }

      return acc;
    },
    { events: {}, cities: {}, countries: {}, device: {}, os: {}, browser: {} }
  );
};

/**
 * Builds a dataset object for charting purposes.
 * @param {Record<string, { total: number }>} data
 * @param {number} max
 * @returns {{
 *   labels: string[],
 *   datasets: Array<{ label: string, data: number[], backgroundColor: string[], borderWidth: number, hoverOffset: number }>
 * }}
 */
export const generateDataset = (data, max) => {
  const entries = Object.entries(data);

  entries.sort((a, b) => b[1].total - a[1].total);

  const sorted = Object.fromEntries(entries);
  const labels = Object.keys(sorted);
  const dataset = labels.map((label) => sorted[label].total);

  return {
    labels: labels.slice(0, max),
    datasets: [
      {
        label: $format('label.TOTAL'),
        data: dataset.slice(0, max),
        backgroundColor: staticHexColors,
        borderWidth: 1,
        hoverOffset: 4
      }
    ]
  };
};

/**
 * Counts occurrences of a field (like sender/recipient) in activity entries.
 * @param {ActivityEntry[]} data
 * @param {string} field
 * @returns {Record<string, number>}
 */
export const groupByEmailCounts = (data, field) => {
  return data.reduce((acc, entry) => {
    const key = entry[field];
    if (!key) return acc;

    acc[key] = acc[key] ? acc[key] + 1 : 1;
    return acc;
  }, {});
};

/**
 * Builds a dataset showing sender/recipient frequency (top 5).
 * @param {ActivityEntry[]} activity
 * @param {string} field
 * @returns {{
 *   labels: string[],
 *   datasets: Array<{ label: string, data: number[], backgroundColor: string[], borderWidth: number, hoverOffset: number }>
 * }}
 */
export const generateSenderRecipientDataset = (activity, field) => {
  const counts = groupByEmailCounts(activity, field);
  const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const top = entries.slice(0, 5);

  return {
    labels: top.map(([email]) => email),
    datasets: [
      {
        label: $format('label.EVENTS'),
        data: top.map(([, count]) => count),
        backgroundColor: staticHexColors,
        borderWidth: 1,
        hoverOffset: 4
      }
    ]
  };
};

/**
 * Groups activity entries by email domain.
 * @param {ActivityEntry[]} activity
 * @returns {Record<string, number>}
 */
export const getDomainDistribution = (activity) => {
  const counts = {};

  for (const item of activity) {
    const domain = item.recipient?.split('@')[1] || unknown;
    counts[domain] = counts[domain] ? counts[domain] + 1 : 1;
  }

  return counts;
};

/**
 * Generates a dataset from domain counts.
 * @param {Record<string, number>} map
 * @param {number} max
 * @returns {{
 *   labels: string[],
 *   datasets: Array<{ label: string, data: number[], backgroundColor: string[], borderWidth: number, hoverOffset: number }>
 * }}
 */
export const generateDomainsDataset = (map, max) => {
  const entries = Object.entries(map).sort((a, b) => b[1] - a[1]);
  const labels = entries.map(([label]) => label);
  const dataset = entries.map(([, value]) => value);

  return {
    labels: labels.slice(0, max),
    datasets: [
      {
        label: $format('label.TOTAL'),
        data: dataset.slice(0, max),
        backgroundColor: staticHexColors,
        borderWidth: 1,
        hoverOffset: 4
      }
    ]
  };
};
